import React, {FormEvent, useCallback, useState, useRef, useEffect} from "react";
import intl from "react-intl-universal";
import {useRootStore, useAuthService, useAppState} from "@stores/AppStateContext";

import type {SignUpData} from "./SignUpFirstStep";

import {AuthModalHeader, logIn} from "../../AuthModal";
import {Button} from "../../../../../components/Button";
import {MaterialUiInput} from "../../../../../components/form/material-ui-input/MaterialUiInput";
import {AWSAPIError} from "../../../../../../utils/types";
import {logErrorToSentry} from "../../../../../../utils/errors/logErrorToSentry";
import {ResendConfirmationCode} from "./ResendConfirmationCode";


import {inAppEventsHub} from "../../../../../../volatile/InAppEventsHub";


export interface SignUpConfirmStepProps {
    setStep: (stepId: number) => void;
    stepData?: SignUpData
    setShowBack: (newValue: boolean) => void;
}

interface FormErrors {
    email?: string;
    confirm_code?: string;
}

export const SignUpConfirmStep = ({stepData, setStep, setShowBack}: SignUpConfirmStepProps) => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const authService = useAuthService();
    const [errors, setErrors] = useState<FormErrors>({});
    const [isProcessing, setIsProcessing] = useState(false);
    const emailFieldRef = useRef<HTMLInputElement>();
    const isSubmittingRef = useRef(false);

    const handleConfirmSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;

        setErrors({});
        setIsProcessing(true);
        try {
            const username = stepData?.username !== undefined
                ? stepData.username
                : (event.target as any).email.value;
            await authService.confirmSignUp(username, (event.target as any).confirm_code.value);
            if (stepData?.password) {
                logIn(appState, rootStore.view, username, stepData?.password).catch(logErrorToSentry);
            } else {
                rootStore.view.openPageByName('sign-in');
            }
            isSubmittingRef.current = false;
            setIsProcessing(false);
        } catch (error: unknown) {
            isSubmittingRef.current = false;
            setIsProcessing(false);
            switch ((error as AWSAPIError).name) {
                case 'CodeMismatchException': {
                    setErrors({
                        confirm_code: intl.get('aws_api_error.CodeMismatchException').d('Invalid verification code provided, please try again.')
                    });
                    break;
                }
                default:
                    setErrors({
                        confirm_code: (error as Error).message
                    });
            }
        }
    }

    const handleBack = useCallback(() => {
        setStep(1);
        setShowBack(false);
    }, [setStep]);

    useEffect(() => {
        inAppEventsHub.subscribe('AuthBack', handleBack);
        return () => {
            inAppEventsHub.unsubscribe('AuthBack', handleBack);
        }
    }, [handleBack]);

    return (
        <form onSubmit={handleConfirmSubmit}>
            <AuthModalHeader
                title={intl.get('sign_up.header_confirm').d('Sign up confirm')}
            />
            <div className='AuthModalBodyStart'>
                {
                    stepData?.username === undefined
                        ? (
                            <MaterialUiInput
                                key="emailField"
                                className={'Auth-input'}
                                isRequired
                                name="email"
                                label={intl.get('sign_up.email').d('Email')}
                                fullWidth={true}
                                error={errors.email}
                                innerRef={emailFieldRef}
                            />
                        ) : (
                            <CheckEmailMessage your_email={stepData.username}/>
                        )
                }
                <div className="Auth-text-line label-large">
                    {intl.get('sign_up.resend_message').d("Didn't receive code?")}
                    <ResendConfirmationCode
                        setErrors={setErrors}
                        getEmail={() => {
                            if (stepData !== undefined) {
                                return stepData.username
                            } else {
                                return (document.getElementById('email') as HTMLInputElement)!.value;
                            }
                        }}
                        emailFieldRef={emailFieldRef}
                    />
                </div>
                <MaterialUiInput
                    key="confirmField"
                    className={'Auth-small-margin Auth-input'}
                    isRequired
                    name="confirm_code"
                    label={intl.get('sign_up.confirm_label').d('Confirmation code')}
                    fullWidth={true}
                    error={errors.confirm_code}
                />
                <Button
                    type="primary"
                    htmlType="submit"
                    fullWidth={true}
                    className={'Auth-input Auth-btn'}
                    text={intl.get('sign_up.confirm_btn').d('Confirm')}
                    loading={isProcessing}
                    size={'lg'}
                />
            </div>
        </form>
    );
}

interface CheckEmailMessageProps {
    your_email?: string;
}

const CheckEmailMessage = ({your_email}: CheckEmailMessageProps) => {
    const emailHolder = intl.get('sign_up.confirm_email_holder').d('your email');
    return (
        <div className="Auth-confirmation-message">
            <div
                className='body-large-bold'>{intl.get('Auth-confirmation-message-header').d('Check your email')}</div>
            <div className='Auth-confirmation-message-header body-large'>
                {intl.getHTML('sign_up.confirm_note', {
                    your_email: your_email ? your_email : emailHolder,
                }).d(`Please enter a confirmation code that was sent to <b>{your_email}</b> to activate your account. If you don't see the email, please check your spam folder.`)}
            </div>
        </div>
    )
}
